import React, { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Search, ChevronLeft, ChevronRight, LayoutDashboard, Database, User, Bell,
  FileText, MessageSquare, Map, KeyRound, Share2, Settings2, Wand2, Activity,
  Download, Trash2, PlusCircle, PencilLine, LogIn, ChevronDown, ShieldCheck, ShieldOff,
} from "lucide-react";

const RES_ICONS = {
  dashboards: LayoutDashboard, datasets: Database, users: User, auth: LogIn,
  alerts: Bell, reports: FileText, comments: MessageSquare, "geo-maps": Map,
  "join-contracts": Share2, tenants: Settings2, ai: Wand2, media: FileText,
  schedules: Bell, exports: Download, access: ShieldCheck,
};
const RES_LABELS = {
  dashboards: "dashboard", datasets: "dataset", users: "user", auth: "session",
  alerts: "alert", reports: "report", comments: "comment", "geo-maps": "custom map",
  "join-contracts": "relationship", tenants: "workspace", media: "file", schedules: "schedule",
  access: "access",
};
const VERB_STYLE = {
  create: "bg-[color:rgba(0,194,168,0.12)] text-[color:var(--success)]",
  update: "bg-[color:rgba(43,78,255,0.10)] text-[color:var(--brand-ocean)]",
  delete: "bg-[color:rgba(220,38,38,0.10)] text-[#DC2626]",
  run: "bg-[color:var(--surface-2)] text-[color:var(--ink-2)]",
  deny: "bg-[color:rgba(220,38,38,0.10)] text-[#DC2626]",
};

// B5 access events carry structured details — render them as sentences.
function accessFriendly(e) {
  const d = e.details || {};
  const who = d.user ? `${d.user}` : d.group ? `group “${d.group}”` : d.principal ? `“${d.principal}”` : "";
  const lvl = d.level ? ` as ${d.level === "none" ? "No access" : d.level}` : "";
  switch (e.action) {
    case "access.denied": return { text: `was denied ${d.action || "access"} on ${d.kind || "a resource"} “${d.name || e.resource_id}”`, kind: "deny", Icon: ShieldOff };
    case "access.dashboard_user_grant": return { text: `set ${who}${lvl} on dashboard “${d.dashboard}”`, kind: "update", Icon: ShieldCheck };
    case "access.dashboard_user_revoke": return { text: `removed ${who} from dashboard “${d.dashboard}”`, kind: "delete", Icon: ShieldOff };
    case "access.dashboard_group_grant": return { text: `set ${who}${lvl} on dashboard “${d.dashboard}”`, kind: "update", Icon: ShieldCheck };
    case "access.dashboard_group_revoke": return { text: `removed ${who} from dashboard “${d.dashboard}”`, kind: "delete", Icon: ShieldOff };
    case "access.dashboard_category": return { text: `set category of “${d.dashboard}” to “${d.to || "none"}”`, kind: "update", Icon: PencilLine };
    case "access.dataset_user_grant":
    case "access.dataset_group_grant": return { text: `shared dataset “${d.dataset}” with ${who}${lvl}`, kind: "update", Icon: ShieldCheck };
    case "access.dataset_user_revoke":
    case "access.dataset_group_revoke": return { text: `unshared dataset “${d.dataset}” from ${who}`, kind: "delete", Icon: ShieldOff };
    case "access.grant_set": return { text: `granted ${who}${lvl} on ${d.scope === "all" ? "all dashboards" : `category “${d.category}”`}`, kind: "update", Icon: ShieldCheck };
    case "access.grant_removed": return { text: `removed the ${d.scope === "all" ? "all-dashboards" : `category “${d.category}”`} grant for ${who}`, kind: "delete", Icon: ShieldOff };
    case "access.group_created": return { text: `created group “${d.group}”`, kind: "create", Icon: PlusCircle };
    case "access.group_updated": return { text: `updated group “${d.group}”`, kind: "update", Icon: PencilLine };
    case "access.group_deleted": return { text: `deleted group “${d.group}”`, kind: "delete", Icon: Trash2 };
    case "access.group_member_added": return { text: `added ${d.user} to group “${d.group}”`, kind: "create", Icon: PlusCircle };
    case "access.group_member_removed": return { text: `removed ${d.user} from group “${d.group}”`, kind: "delete", Icon: Trash2 };
    default: return { text: e.action || "changed access", kind: "update", Icon: ShieldCheck };
  }
}

// Turn a raw audit record into a human sentence + verb kind.
function friendly(e) {
  const path = e.path || "";
  const res = e.resource_type || "";
  const label = RES_LABELS[res] || res || "resource";
  const seg = (s) => path.includes(`/${s}`);
  if (res === "access") return accessFriendly(e);
  if (res === "auth") {
    if (seg("login")) return { text: "signed in", kind: "run", Icon: LogIn };
    if (seg("signup")) return { text: "created an account", kind: "create", Icon: User };
    return { text: "changed account settings", kind: "update", Icon: KeyRound };
  }
  const Icon = RES_ICONS[res] || Activity;
  if (seg("query") || seg("distinct") || seg("field-values")) return { text: `ran a query on a ${label}`, kind: "run", Icon: Activity };
  if (seg("rows")) return { text: `viewed underlying rows of a ${label}`, kind: "run", Icon: Activity };
  if (seg("export")) return { text: `exported a ${label}`, kind: "run", Icon: Download };
  if (seg("prep/materialize")) return { text: "saved a prepared dataset", kind: "create", Icon: Database };
  if (seg("rematerialize")) return { text: "refreshed a prepared dataset", kind: "run", Icon: Activity };
  if (seg("prep")) return { text: "edited data-prep steps", kind: "update", Icon: PencilLine };
  if (seg("share")) return { text: `shared a ${label}`, kind: "update", Icon: Share2 };
  if (seg("duplicate")) return { text: `duplicated a ${label}`, kind: "create", Icon: PlusCircle };
  if (seg("refresh")) return { text: `refreshed a ${label}`, kind: "run", Icon: Activity };
  const m = (e.method || (e.action || "").split(".").pop() || "").toLowerCase();
  if (m === "delete") return { text: `deleted a ${label}`, kind: "delete", Icon: Trash2 };
  if (m === "post") return { text: `created a ${label}`, kind: "create", Icon: PlusCircle };
  if (m === "put" || m === "patch") return { text: `updated a ${label}`, kind: "update", Icon: PencilLine };
  return { text: e.action || "did something", kind: "run", Icon: Activity };
}

function relTime(ts) {
  const d = (Date.now() - new Date(ts).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 7 * 86400) return `${Math.floor(d / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function AdminAudit() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [resource, setResource] = useState("all");
  const [who, setWho] = useState("all");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [openId, setOpenId] = useState(null);
  const limit = 25;

  const load = useCallback(() => {
    api.get("/audit", { params: { q, status: status === "all" ? "" : status, resource: resource === "all" ? "" : resource, staff_only: who === "staff", limit, offset: page * limit } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [q, status, resource, who, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / limit));
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[color:var(--ink-3)]" />
          <Input data-testid="audit-search" className="pl-8 bg-[color:var(--surface)]" placeholder="Search actions or paths…" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        </div>
        <Select value={resource} onValueChange={(v) => { setResource(v); setPage(0); }}>
          <SelectTrigger data-testid="audit-resource-filter" className="w-[150px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All activity</SelectItem>
            <SelectItem value="access" data-testid="audit-resource-access">Access (permissions & denials)</SelectItem>
            {Object.entries(RES_LABELS).filter(([k]) => k !== "access").map(([k, v]) => <SelectItem key={k} value={k}>{v[0].toUpperCase() + v.slice(1)}s</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
          <SelectTrigger data-testid="audit-status-filter" className="w-[130px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="ok">Success</SelectItem>
            <SelectItem value="error">Errors</SelectItem>
          </SelectContent>
        </Select>
        <Select value={who} onValueChange={(v) => { setWho(v); setPage(0); }}>
          <SelectTrigger data-testid="audit-actor-filter" className="w-[170px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            <SelectItem value="staff" data-testid="audit-actor-staff">Eazy staff only</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-[color:var(--ink-3)]">{data.total.toLocaleString()} events</span>
      </div>
      <div className="glass-card divide-y divide-[color:var(--glass-border)]">
        {data.items.map((e) => {
          const f = friendly(e);
          const isErr = e.status !== "ok";
          const open = openId === e.id;
          return (
            <div key={e.id} data-testid="audit-row">
              <button onClick={() => setOpenId(open ? null : e.id)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[color:var(--surface-2)] transition-colors">
                <span className={cn("w-7 h-7 rounded-lg flex items-center justify-center shrink-0", VERB_STYLE[f.kind])}>
                  <f.Icon className="w-3.5 h-3.5" />
                </span>
                <span className="flex-1 min-w-0 text-sm text-[color:var(--ink-2)] truncate">
                  <b className="text-[color:var(--ink)]">{e.user_email || "Someone"}</b> {f.text}
                  {e.staff && <span data-testid="audit-staff-chip" className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:rgba(43,78,255,0.10)] text-[color:var(--brand-ocean)] font-medium">Eazy staff</span>}
                  {isErr && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:rgba(220,38,38,0.10)] text-[#DC2626] font-medium">failed{String(e.status).includes(":") ? ` · ${String(e.status).split(":")[1]}` : ""}</span>}
                </span>
                <span className="text-[11px] text-[color:var(--ink-3)] whitespace-nowrap" title={new Date(e.ts).toLocaleString()}>{relTime(e.ts)}</span>
                <ChevronDown className={cn("w-3.5 h-3.5 text-[color:var(--ink-3)] transition-transform", open && "rotate-180")} />
              </button>
              {open && (
                <div data-testid="audit-row-detail" className="px-13 pb-2.5 pl-[52px] text-[11px] text-[color:var(--ink-3)] space-y-0.5">
                  <div><span className="font-medium text-[color:var(--ink-2)]">When:</span> {new Date(e.ts).toLocaleString()}</div>
                  <div><span className="font-medium text-[color:var(--ink-2)]">Request:</span> <code className="text-[10px]">{e.method} {e.path}</code></div>
                  <div><span className="font-medium text-[color:var(--ink-2)]">Action code:</span> <code className="text-[10px]">{e.action}</code> · <span className="font-medium text-[color:var(--ink-2)]">Status:</span> {e.status}</div>
                  {e.staff && <div data-testid="audit-staff-detail"><span className="font-medium text-[color:var(--ink-2)]">Eazy staff:</span> acting in this workspace as <code className="text-[10px]">{e.actor_role}</code></div>}
                  {e.details && Object.keys(e.details).length > 0 && <div><span className="font-medium text-[color:var(--ink-2)]">Details:</span> <code className="text-[10px]">{JSON.stringify(e.details)}</code></div>}
                </div>
              )}
            </div>
          );
        })}
        {data.items.length === 0 && <div className="px-3 py-10 text-center text-xs text-[color:var(--ink-3)]">No audit events yet — activity will appear here as your team works.</div>}
      </div>
      <div className="flex items-center justify-end gap-2 text-xs text-[color:var(--ink-3)]">
        <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
        Page {page + 1} / {pages}
        <Button size="sm" variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next-page"><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
